import { ApiError } from './errors';

/**
 * 🔴 A 404 IS TWO COMPLETELY DIFFERENT FACTS, AND THE API LETS US TELL THEM APART.
 *
 *   · `{"error":"not_found","message":"no such route"}`      → the ROUTE is not deployed
 *   · `{"error":"not_found","message":"project not found"}`  → the ROUTE is there, the ID is wrong
 *
 * The first is "this half of the product is not built yet"; the second is "you asked for something
 * that does not exist". They lead to entirely different actions — one is a message to a developer,
 * the other a message to the reader — so collapsing both into "Not found" throws away the only
 * useful half.
 *
 * ⭐ `setNotFoundHandler` in the API's `app.ts` is the ONLY thing that emits `no such route`; every
 * route's own 404 names the entity it could not find. So this test is exact rather than a heuristic.
 *
 * ⚠️ `dev/mock-api.mjs` was saying `mock has no route for /x` and therefore did NOT match — a
 * fixture divergence that made every unbuilt-endpoint view look like an ordinary 404 locally. The
 * fixture now mirrors the API's wording; this function is deliberately not taught about the fixture.
 */
export function isMissingRoute(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.kind !== 'not_found') return false;
  return (error.reason ?? '').toLowerCase().includes('no such route');
}

/**
 * The shape every "this endpoint may not be deployed" fetch returns.
 *
 * ⛔ NOT an empty list and NOT a thrown error. An empty list is a claim about the data ("there are
 * none"), which is precisely what an absent endpoint cannot support; a bare throw loses the
 * distinction this module exists to preserve. The path rides along so the view can NAME what is
 * missing instead of hand-waving at it.
 */
export type MaybeMissing<T> = { kind: 'ok'; value: T } | { kind: 'endpoint-missing'; path: string };

/** Runs a fetch, converting only the API's own "no such route" 404 into `endpoint-missing`. */
export async function tolerateMissingRoute<T>(
  path: string,
  run: () => Promise<T>,
): Promise<MaybeMissing<T>> {
  try {
    return { kind: 'ok', value: await run() };
  } catch (err) {
    if (isMissingRoute(err)) return { kind: 'endpoint-missing', path: `/api/v1${path}` };
    throw err;
  }
}
